// Practice setup (§9.2): an offline LocalSession on any arena and mode, 0–7 bots, a difficulty,
// dummies on/off. In Practice, keys 1–6 (or the touch item wheel) grant items and Esc offers
// Restart or Leave.
import { Difficulty, ItemKind, MapId, ModeId } from '@shared/ids';
import type { LiveScreen } from '../contracts';
import type { PracticeSetup, UiDeps } from '../deps';
import { icon, ITEM_ICONS } from '../icons';
import { DIFFICULTY_NAMES, ITEM_NAMES } from '../labels';
import { el } from '../ui';
import { button, choice, closeButton, field, stepper, toggle } from '../widgets';
import { mapPicker, modePicker } from './pickers';

const ITEMS: readonly ItemKind[] = [ItemKind.BANANA, ItemKind.BOMB, ItemKind.GLOVE, ItemKind.BALLOON, ItemKind.PLUNGER, ItemKind.SHAKE];

export function create(deps: UiDeps): LiveScreen {
  const setup: PracticeSetup = { mapId: MapId.TEACUP, modeId: ModeId.ROYALE, bots: 3, difficulty: Difficulty.NORMAL, dummies: true };
  const map = mapPicker(setup.mapId, (v) => {
    if (v !== 'rotation') setup.mapId = v;
  }, false);
  const mode = modePicker(setup.modeId, (v) => {
    setup.modeId = v;
  });
  const bots = stepper('bots', 0, 7, setup.bots, (n) => String(n), (v) => {
    setup.bots = v;
  });
  const difficulty = choice('Bot difficulty', [Difficulty.CHILL, Difficulty.NORMAL, Difficulty.SPICY].map((d) => ({ value: d, label: DIFFICULTY_NAMES[d] })), setup.difficulty, (v) => {
    setup.difficulty = v;
  });
  const dummies = toggle('Dummies', setup.dummies, (v) => {
    setup.dummies = v;
  });

  const form = el('form', { class: 'panel sheet with-ribbon practice-panel springy', attrs: { novalidate: true }, on: {
    submit: (e) => {
      e.preventDefault();
      deps.actions.practice({ ...setup });
    },
  } },
    el('div', { class: 'ribbon mint', text: 'PRACTICE' }),
    closeButton(() => deps.nav.back()),
    el('div', { class: 'sheet-body' },
      el('div', { class: 'field stacked' }, el('div', { class: 'flabel' }, el('span', { text: 'Arena' })), map.el),
      el('div', { class: 'field stacked' }, el('div', { class: 'flabel' }, el('span', { text: 'Mode' })), mode.el),
      // Side by side on wide screens: the whole setup fits a 16:9 screen without scrolling.
      el('div', { class: 'field-pair' }, field('Bots', bots.el, '0–7 sparring partners'), field('Bot difficulty', difficulty.el)),
      field('Dummies', dummies.el, 'Punching bags on springs'),
      el('div', { class: 'practice-items' },
        el('div', { class: 'flabel' }, el('span', { text: 'Item hotkeys' }), el('small', { text: 'Grab any item any time' })),
        el('div', { class: 'item-keys' }, ...ITEMS.map((k, i) => el('span', { class: 'item-key', attrs: { title: ITEM_NAMES[k] } },
          el('span', { class: 'key', text: String(i + 1) }), icon(ITEM_ICONS[k])))))),
    el('div', { class: 'sheet-foot row center' },
      button({ label: 'BACK', icon: 'back', variant: 'cream', onClick: () => deps.nav.back() }),
      button({ label: 'START', icon: 'play', variant: 'pink', type: 'submit', autofocus: true })));

  return { el: el('div', { class: 'screen modal' }, form) };
}
